import { useMemo, useState } from "react";
import {
  AlertCircle,
  CheckCircle2,
  ExternalLink,
  EyeOff,
  Loader2,
  Pencil,
  Plus,
  Scale,
  Search,
  Trash2,
  XCircle,
} from "lucide-react";
import {
  useDeleteLegalReference,
  useHideBuiltinLegalReference,
  useLegalCatalog,
  type LegalReference,
} from "@/hooks/use-legal";
import { CustomLegalReferenceDialog } from "@/components/configuracion/custom-legal-reference-dialog";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";

// Mejora N2 (changelog cliente): página de marco legal vigente.
// Catálogo curado de normativa relevante para licitaciones públicas y CIMI
// con links al BOE oficial. Acompaña al verificador legal que se ejecuta
// desde el editor de propuesta.

const CATEGORY_LABELS: Record<string, string> = {
  contratacion_publica: "Contratación pública",
  procedimiento_administrativo: "Procedimiento administrativo",
  subvenciones: "Subvenciones",
  regimen_local: "Régimen local",
  menores: "Menores y justicia juvenil",
  proteccion_datos: "Protección de datos",
  laboral: "Laboral",
  igualdad: "Igualdad",
  servicios_sociales: "Servicios sociales",
};

function statusBadge(status: string) {
  if (status === "vigente") {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-emerald-100 text-emerald-700">
        <CheckCircle2 className="w-3 h-3" />
        Vigente
      </span>
    );
  }
  if (status === "derogada") {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-red-100 text-red-700">
        <XCircle className="w-3 h-3" />
        Derogada
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-amber-100 text-amber-800">
      <AlertCircle className="w-3 h-3" />
      Modificada
    </span>
  );
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

export default function MarcoLegalPage() {
  const { data, isLoading, isError } = useLegalCatalog();
  const deleteMutation = useDeleteLegalReference();
  const hideMutation = useHideBuiltinLegalReference();
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<LegalReference | null>(null);
  const [pendingDelete, setPendingDelete] = useState<LegalReference | null>(null);

  const categories = useMemo(() => {
    if (!data) return [];
    return Array.from(new Set(data.laws.map((law) => law.category))).sort();
  }, [data]);

  const filteredLaws = useMemo(() => {
    if (!data) return [] as LegalReference[];
    const term = search.trim().toLowerCase();
    return data.laws.filter((law) => {
      if (categoryFilter !== "all" && law.category !== categoryFilter)
        return false;
      if (!term) return true;
      return (
        law.title.toLowerCase().includes(term) ||
        law.short_title.toLowerCase().includes(term) ||
        law.number.toLowerCase().includes(term) ||
        law.aliases?.some((a) => a.toLowerCase().includes(term))
      );
    });
  }, [data, search, categoryFilter]);

  return (
    <div className="p-6 max-w-6xl">
      <div className="flex items-start justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Scale className="w-6 h-6 text-blue-600" />
            <h1 className="text-2xl font-bold text-gray-900">
              Marco legal vigente
            </h1>
          </div>
          <p className="text-sm text-gray-500 max-w-3xl">
            Catálogo curado de normativa relevante para licitaciones públicas y
            centros de internamiento de menores. Cada entrada enlaza al BOE
            oficial; el verificador legal del editor de propuesta usa esta
            misma lista para detectar referencias desactualizadas en tus
            secciones.
          </p>
          {data && (
            <p className="text-xs text-gray-400 mt-2">
              Catálogo {data.version} · {data.total} normas · última
              actualización {formatDate(data.updated_at)}
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={() => {
            setEditing(null);
            setEditorOpen(true);
          }}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
        >
          <Plus className="w-4 h-4" />
          Añadir norma
        </button>
      </div>

      {/* Filtros */}
      {data && (
        <div className="flex items-center gap-3 mb-4 flex-wrap">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por título, número o alias (LCSP, Ley 9/2017…)"
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">Todas las categorías</option>
            {categories.map((cat) => (
              <option key={cat} value={cat}>
                {CATEGORY_LABELS[cat] ?? cat}
              </option>
            ))}
          </select>
          <span className="text-xs text-gray-500">
            {filteredLaws.length} de {data.total}
          </span>
        </div>
      )}

      {isLoading && (
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Loader2 className="w-4 h-4 animate-spin" />
          Cargando catálogo…
        </div>
      )}

      {isError && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
          <AlertCircle className="w-4 h-4" />
          Error cargando el catálogo. Recarga la página.
        </div>
      )}

      {data && (
        <div className="overflow-x-auto border border-gray-200 rounded-lg">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-gray-700">
                  Norma
                </th>
                <th className="px-4 py-3 text-left font-medium text-gray-700">
                  Categoría
                </th>
                <th className="px-4 py-3 text-left font-medium text-gray-700">
                  Estado
                </th>
                <th className="px-4 py-3 text-left font-medium text-gray-700">
                  Última consolidación
                </th>
                <th className="px-4 py-3 text-left font-medium text-gray-700">
                  BOE
                </th>
                <th className="px-4 py-3 text-right font-medium text-gray-700 w-24">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {filteredLaws.map((law) => (
                <tr key={law.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 align-top">
                    <div className="font-semibold text-gray-900 flex items-center gap-2 flex-wrap">
                      <span>
                        {law.short_title}{" "}
                        <span className="text-gray-400 font-normal">
                          · {law.number}
                        </span>
                      </span>
                      {law.is_built_in === false && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-100 text-blue-700">
                          Personalizada
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-600 mt-0.5">
                      {law.title}
                    </div>
                    {law.aliases && law.aliases.length > 0 && (
                      <div className="text-[11px] text-gray-400 mt-1">
                        Alias: {law.aliases.slice(0, 4).join(" · ")}
                        {law.aliases.length > 4 && " · …"}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 align-top text-xs text-gray-600">
                    {CATEGORY_LABELS[law.category] ?? law.category}
                  </td>
                  <td className="px-4 py-3 align-top">
                    {statusBadge(law.status)}
                    {law.status === "derogada" && law.superseded_by && (
                      <div className="text-[11px] text-gray-500 mt-1">
                        Sustituida por{" "}
                        <span className="font-medium">
                          {data.laws.find((l) => l.id === law.superseded_by)
                            ?.short_title ?? law.superseded_by}
                        </span>
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 align-top text-xs text-gray-600">
                    {formatDate(law.consolidated_date)}
                  </td>
                  <td className="px-4 py-3 align-top">
                    <a
                      href={law.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 text-xs"
                    >
                      {law.boe_id ?? "Ver fuente"}
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </td>
                  <td className="px-4 py-3 align-top text-right">
                    {law.is_built_in === false ? (
                      <div className="inline-flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => {
                            setEditing(law);
                            setEditorOpen(true);
                          }}
                          className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded"
                          aria-label="Editar norma"
                          title="Editar"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setPendingDelete(law)}
                          className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded"
                          aria-label="Eliminar norma"
                          title="Eliminar"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ) : (
                      <div className="inline-flex items-center gap-2">
                        <span className="text-[11px] text-gray-400">
                          Predefinida
                        </span>
                        <button
                          type="button"
                          onClick={() => setPendingDelete(law)}
                          className="p-1.5 text-gray-500 hover:text-amber-600 hover:bg-amber-50 rounded"
                          aria-label="Ocultar norma del catálogo"
                          title="Ocultar del catálogo"
                        >
                          <EyeOff className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
              {filteredLaws.length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    className="px-4 py-8 text-center text-sm text-gray-500"
                  >
                    No hay normas que coincidan con el filtro.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <CustomLegalReferenceDialog
        isOpen={editorOpen}
        onClose={() => {
          setEditorOpen(false);
          setEditing(null);
        }}
        reference={editing}
      />

      <ConfirmDialog
        open={!!pendingDelete}
        onOpenChange={(open) => {
          if (!open) setPendingDelete(null);
        }}
        title={
          pendingDelete?.is_built_in
            ? "Ocultar norma predefinida"
            : "Eliminar norma personalizada"
        }
        description={
          pendingDelete
            ? pendingDelete.is_built_in
              ? `¿Ocultar “${pendingDelete.short_title}” del catálogo? Dejará de aparecer y de usarse en el verificador legal. Puedes restaurarla más adelante.`
              : `¿Eliminar “${pendingDelete.short_title}” del catálogo? Esta acción no afecta a propuestas históricas que la hayan citado.`
            : ""
        }
        variant="destructive"
        confirmLabel={pendingDelete?.is_built_in ? "Ocultar" : "Eliminar"}
        loading={deleteMutation.isPending || hideMutation.isPending}
        onConfirm={() => {
          if (!pendingDelete) return;
          const mutation = pendingDelete.is_built_in
            ? hideMutation
            : deleteMutation;
          mutation.mutate(pendingDelete.id, {
            onSuccess: () => setPendingDelete(null),
          });
        }}
      />
    </div>
  );
}
